import React, { type FC, useLayoutEffect, useRef } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

import { type DeviationEventToastItem, NotificationSeverity, selectDeviationCounterToast, selectDeviationEventToasts, Translation } from '../../../domain'

import { useTranslator } from '../Internationalization'
import { useAppState, useAppUsecase } from '../State'
import { notificationToastAutoHideDurationMs, notistackDefaultBottomOffsetPx, notistackDefaultRightOffsetPx, Toast } from '../common/Notifications'

const EVENT_TOAST_DURATION_MS = 4_000
const COUNTER_TOAST_DURATION_MS = notificationToastAutoHideDurationMs[NotificationSeverity.WARNING]

/** Matches notistack's own inter-snack gap (`indents.snackbar.default` = 6px). This theme's spacing unit is 4px, so `1.5` = 6px. */
const ZONE_GAP = 1.5

/** Published on `document.documentElement`; `AppWrapper.tsx` reads it to shift the general Toast zone
 * up out of the way. A CSS variable, not a prop/import, since the two zones live in separate feature folders. */
export const DEVIATION_ZONE_HEIGHT_CSS_VAR = '--deviation-toast-zone-height'

const zoneStyle: SxProps<Theme> = {
    position: 'fixed',
    bottom: notistackDefaultBottomOffsetPx,
    right: notistackDefaultRightOffsetPx,
    display: 'flex',
    flexDirection: 'column',
    gap: ZONE_GAP,
    zIndex: (theme) => theme.zIndex.snackbar,
}

/** Independent per-event timer - never reset by another event arriving/leaving - falls out for free here: each event keeps its own `key={item.id}` at the call site below, so a new event is a fresh `Toast` instance with its own auto-hide timer, never sharing or resetting an existing one. */
const DeviationEventToast: FC<Readonly<{ item: DeviationEventToastItem }>> = ({ item }) => {
    const translator = useTranslator()
    const deviationToastsUsecase = useAppUsecase('deviationToastsUsecase')

    return (
        <Toast
            severity={item.kind === 'new' ? NotificationSeverity.WARNING : NotificationSeverity.SUCCESS}
            title={translator.formatTranslation(item.kind === 'new' ? Translation.DEVIATION_NEW_TOAST : Translation.DEVIATION_RESOLVED, {
                id: item.serviceId,
            })}
            autoHideDurationMs={EVENT_TOAST_DURATION_MS}
            onClose={() => deviationToastsUsecase.dismissEvent(item.id)}
        />
    )
}

/** No close button (Figma) - `onAutoHide` is `Toast`'s only path to `dismissCounter()`, since there's no `onClose` to fall back on. `Toast` itself resets its auto-hide timer whenever `count` changes, re-announcing a changed total for a fresh 8s. */
const DeviationCounterToast: FC<Readonly<{ count: number }>> = ({ count }) => {
    const translator = useTranslator()
    const deviationToastsUsecase = useAppUsecase('deviationToastsUsecase')

    return (
        <Toast
            severity={NotificationSeverity.WARNING}
            title={translator.formatTranslation(Translation.DEVIATION_TOAST_COUNTER, { count })}
            count={count}
            autoHideDurationMs={COUNTER_TOAST_DURATION_MS}
            onAutoHide={() => deviationToastsUsecase.dismissCounter()}
        />
    )
}

/**
 * Bespoke (no notistack) second toast zone for the deviation counter + event cascade. Rendered
 * top-to-bottom as `[...events, counter]`: `deviationEventToasts` is already ordered oldest-first, so
 * this puts the oldest event at the top and the newest just above the counter.
 *
 * No `ResizeObserver` - height is re-measured synchronously whenever the queue's shape changes
 * instead, which covers every case this app's desktop-only, non-responsive layout needs.
 */
export const DeviationToastZone: FC = () => {
    const events = useAppState(selectDeviationEventToasts)
    const counter = useAppState(selectDeviationCounterToast)
    const containerRef = useRef<HTMLDivElement>(null)

    /** Depends on `events` itself, not `.length` - at the 3-item cap a new event replaces the oldest one in place, so length alone wouldn't change. */
    useLayoutEffect(() => {
        document.documentElement.style.setProperty(DEVIATION_ZONE_HEIGHT_CSS_VAR, `${containerRef.current?.offsetHeight ?? 0}px`)
        return () => document.documentElement.style.setProperty(DEVIATION_ZONE_HEIGHT_CSS_VAR, '0px')
    }, [events, counter])

    if (events.length === 0 && !counter) {
        return null
    }

    return (
        <Box data-testid="deviation-toast-zone" ref={containerRef} sx={zoneStyle}>
            {events.map((item) => <DeviationEventToast key={item.id} item={item} />)}
            {counter && <DeviationCounterToast count={counter.count} />}
        </Box>
    )
}
